"use client";

import { SK } from "@/constants";
import { isValidJSON } from "@/utilities/checker";
import React, { createContext, useState, useContext, useEffect } from "react";
import { usePathname } from "next/navigation";

// === Types ===
export interface User {
  id: string;
  username: string;
  email: string;
  token?: string;
  cartinfo?: any;
  [key: string]: any;
  name?: string;
  mobile?: string;
  dob?: string;
  gender?: string;
  profile_pic?: string;
}

interface Config {
  counter: number;
}

export interface CartInfoDefaults {
  cartcount: number;
  cartquantitycount: number;
  products: any[];
  cartprice: number;
  subtotal: number;
  mrptotal: number;
  shipping: number;
  ordertotal: number;
  total: number;
  shippingrate: string;
}

// === Context Type ===
interface UserContextType {
  user: User | null;
  isLoggedIn: boolean;
  login: (userData: User) => void;
  logout: () => void;
  incCounter: (counter: number) => void;
  decCounter: (counter: number) => void;
  config: Config | null;
  setConfig: (config: Config) => void;
  cartinfoData: CartInfoDefaults | null;
  setCartInfoData: (value: CartInfoDefaults) => void;
  loading: boolean;
}

// === Context ===
const UserContext = createContext<UserContextType | undefined>(undefined);

// === Hook ===
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

// === Provider ===
export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [cartinfoData, setCartInfoDataInternal] = useState<CartInfoDefaults | null>(null);
  const [config, setConfig] = useState<Config | null>({ counter: 0 });
  const [loading, setLoading] = useState(true);
  const pathname = usePathname();

  // === Load from localStorage ===
  const loadUserFromStorage = () => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(SK.NEXTUSERINFODATA);
      if (stored && isValidJSON(stored)) {
        setUser(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to load user from localStorage", e);
    }
  };

  const loadCartFromStorage = () => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(SK.NEXTCARTINFODATA);
      if (stored && isValidJSON(stored)) {
        setCartInfoDataInternal(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to load cart from localStorage", e);
    }
  };

  // === Rehydrate on mount + route change ===
  useEffect(() => {
    loadUserFromStorage();
    loadCartFromStorage();
    setLoading(false);
  }, [pathname]);

  // === Login ===
  const login = (userData: User) => {
    setUser(userData);
    if (typeof window !== "undefined") {
      localStorage.setItem(SK.NEXTUSERINFODATA, JSON.stringify(userData));
    }
  };

  // === Logout ===
  const logout = () => {
    setUser(null);
    if (typeof window !== "undefined") {
      localStorage.removeItem(SK.NEXTUSERINFODATA);
      localStorage.removeItem(SK.NEXTCARTINFODATA);
      setCartInfoDataInternal(null);
    }
  };

  // === Cart Update (with localStorage) ===
  const setCartInfoData = (value: CartInfoDefaults) => {
    if (typeof window === "undefined") return;
    const updated = cartinfoData ? { ...cartinfoData, ...value } : value;
    setCartInfoDataInternal(updated);
    localStorage.setItem(SK.NEXTCARTINFODATA, JSON.stringify(updated));
  };

  // === Counter API ===
  const incCounter = async (count: number) => {
    try {
      const counter = count + 1;
      const res = await fetch("/api/counter", {
        method: "POST",
        body: JSON.stringify({ counter }),
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.config) setConfig(data.config);
    } catch (error) {
      console.error("Increment failed:", error);
    }
  };

  const decCounter = async (count: number) => {
    try {
      const counter = count - 1;
      const res = await fetch("/api/counter", {
        method: "POST",
        body: JSON.stringify({ counter }),
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.config) setConfig(data.config);
    } catch (error) {
      console.error("Decrement failed:", error);
    }
  };

  // === Provider Value ===
  const value: UserContextType = {
    user,
    isLoggedIn: !!user,
    login,
    logout,
    cartinfoData,
    setCartInfoData,
    config,
    setConfig,
    incCounter,
    decCounter,
    loading,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};